import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useWebRTC } from './WebRTCContext';

/**
 * SocketContext - Manages gem socket slots for bound sessions
 *
 * Socket States:
 * - EMPTY: Slot is available, shows + icon to generate pairing code
 * - PENDING: Pairing code generated and waiting to be claimed
 * - BOUND_OFFLINE: Session is bound but desktop is offline (dim gem)
 * - BOUND_ONLINE: Session is bound and desktop is online (glowing gem)
 *
 * Each slot can have its own unique pairing code, allowing multiple
 * codes to be generated and pending simultaneously.
 *
 * Selection State:
 * - selectedSocketIndex: Which socket is currently active for UI targeting
 * - All features/commands target the session bound to the selected socket
 */

export const SOCKET_STATE = {
  EMPTY: 'empty',
  PENDING: 'pending',
  BOUND_OFFLINE: 'bound_offline',
  BOUND_ONLINE: 'bound_online',
  // Legacy aliases for backward compatibility
  AVAILABLE: 'empty',
  BOUND_DISCONNECTED: 'bound_offline',
  BOUND_CONNECTED: 'bound_online'
};

const DEFAULT_SLOT_COUNT = 5;
const STORAGE_KEY = 'inquisitor_socket_slots';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const {
    boundSessions,
    isConnected: wsConnected,
    generatePairingCode: webrtcGeneratePairingCode,
    generatedCode,
    codeExpiresAt,
    codeExpiresIn,
    clearGeneratedCode,
    unbindSession,
    registerSelectedPeerIdGetter,
    hasActiveDataChannel
  } = useWebRTC();

  // Per-slot pairing codes: Map<slotIndex, { code, expiresAt, expiresIn }>
  const [slotPairingCodes, setSlotPairingCodes] = useState(() => new Map());

  // Track which slot requested the current pairing code
  const pendingSlotRef = useRef(null);

  // Track whether bound sessions have been received at least once
  // Prevents premature deselection during the gap between WS connect and bound_sessions response
  const boundSessionsLoadedRef = useRef(false);

  // Currently selected socket index (null = none selected)
  const [selectedSocketIndex, setSelectedSocketIndex] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY + '_selected');
      if (saved !== null) {
        const index = parseInt(saved, 10);
        if (!isNaN(index) && index >= 0 && index < DEFAULT_SLOT_COUNT) {
          return index;
        }
      }
    } catch (e) {}
    return null;
  });

  // Persist selected socket
  useEffect(() => {
    if (selectedSocketIndex !== null) {
      localStorage.setItem(STORAGE_KEY + '_selected', selectedSocketIndex.toString());
    } else {
      localStorage.removeItem(STORAGE_KEY + '_selected');
    }
  }, [selectedSocketIndex]);

  // Track when bound sessions have been loaded
  useEffect(() => {
    if (boundSessions.length > 0) {
      boundSessionsLoadedRef.current = true;
    }
    if (!wsConnected) {
      boundSessionsLoadedRef.current = false;
    }
  }, [boundSessions, wsConnected]);

  // When a new pairing code is generated, assign it to the pending slot
  useEffect(() => {
    if (generatedCode && pendingSlotRef.current !== null) {
      const slotIndex = pendingSlotRef.current;
      console.log(`[SocketContext] Assigning pairing code ${generatedCode} to slot ${slotIndex}`);

      setSlotPairingCodes(prev => {
        const newMap = new Map(prev);
        newMap.set(slotIndex, {
          code: generatedCode,
          expiresAt: codeExpiresAt,
          expiresIn: codeExpiresIn
        });
        return newMap;
      });

      // Clear the pending slot tracker
      pendingSlotRef.current = null;
    }
  }, [generatedCode, codeExpiresAt, codeExpiresIn]);

  // Update expiration timers for pending codes
  useEffect(() => {
    if (slotPairingCodes.size === 0) return;

    const interval = setInterval(() => {
      const now = Date.now();
      let hasChanges = false;

      setSlotPairingCodes(prev => {
        const newMap = new Map(prev);

        prev.forEach((codeData, slotIndex) => {
          if (codeData.expiresAt && now >= codeData.expiresAt) {
            // Code has expired
            console.log(`[SocketContext] Pairing code for slot ${slotIndex} expired`);
            newMap.delete(slotIndex);
            hasChanges = true;
          } else if (codeData.expiresAt) {
            // Update remaining time
            const remaining = Math.ceil((codeData.expiresAt - now) / 1000);
            if (remaining !== codeData.expiresIn) {
              newMap.set(slotIndex, { ...codeData, expiresIn: remaining });
              hasChanges = true;
            }
          }
        });

        return hasChanges ? newMap : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [slotPairingCodes.size]);

  // Track previous bound sessions count to detect new sessions
  const prevBoundSessionsCountRef = useRef(0);

  // Clear pairing code for a slot when a new session is bound
  useEffect(() => {
    // If we have more sessions than before, a new session was just bound
    if (boundSessions.length > prevBoundSessionsCountRef.current) {
      // Find the slot index of the new session (it should be at the end of the array
      // or we can check which slots have pending codes that should be cleared)
      const slotsWithPendingCodes = Array.from(slotPairingCodes.keys());

      slotsWithPendingCodes.forEach(slotIndex => {
        // If there's now a session at this slot index, clear the pending code
        if (boundSessions[slotIndex]) {
          console.log(`[SocketContext] Session bound to slot ${slotIndex}, clearing pending code`);
          setSlotPairingCodes(prev => {
            const newMap = new Map(prev);
            newMap.delete(slotIndex);
            return newMap;
          });
        }
      });
    }

    prevBoundSessionsCountRef.current = boundSessions.length;
  }, [boundSessions, slotPairingCodes]);

  // Compute socket states based on bound sessions and pending codes
  const sockets = Array.from({ length: DEFAULT_SLOT_COUNT }, (_, index) => {
    // Bound sessions take priority over pending codes
    const session = boundSessions[index];
    if (session) {
      // Check if session is online via WebSocket (backend knows about it)
      const isWsOnline = session.status === 'online';

      // Check if we have an active DataChannel to this peer
      // Use hasActiveDataChannel which checks the ref directly rather than React state
      // This prevents state desync issues where onclose fires but channel is still active
      const dcActive = hasActiveDataChannel(session.connectionId);

      // For gem state: show as online if we have DataChannel (direct P2P connection)
      // This ensures the gem reflects actual ability to send commands
      const isOnline = dcActive;

      return {
        index,
        state: isOnline ? SOCKET_STATE.BOUND_ONLINE : SOCKET_STATE.BOUND_OFFLINE,
        sessionId: session.sessionId,
        sessionName: session.name || session.sessionId?.substring(0, 8),
        shortName: (session.name || session.sessionId)?.substring(0, 8),
        connectionId: session.connectionId,
        boundAt: session.boundAt,
        role: session.role || 'full', // Capability tier of this pairing
        isSelected: selectedSocketIndex === index,
        // Connection status indicators for the two-part status bar
        isWsOnline,      // Left indicator: WebSocket/signaling connection to backend
        hasDataChannel: dcActive,  // Right indicator: Direct P2P DataChannel connection
        // Legacy fields for compatibility with existing code
        peerId: session.connectionId,
        peerName: session.name,
        shortId: session.connectionId?.substring(0, 8)
      };
    }

    // Check if this slot has a pending pairing code
    const pendingCode = slotPairingCodes.get(index);
    if (pendingCode) {
      return {
        index,
        state: SOCKET_STATE.PENDING,
        sessionId: null,
        sessionName: null,
        pairingCode: pendingCode.code,
        codeExpiresAt: pendingCode.expiresAt,
        codeExpiresIn: pendingCode.expiresIn,
        isSelected: selectedSocketIndex === index,
        // Legacy fields
        peerId: null,
        peerName: null
      };
    }

    // Empty slot
    return {
      index,
      state: SOCKET_STATE.EMPTY,
      sessionId: null,
      sessionName: null,
      isSelected: selectedSocketIndex === index,
      // Legacy fields
      peerId: null,
      peerName: null
    };
  });

  // Get the currently selected session (for targeting)
  const selectedSocket = selectedSocketIndex !== null ? sockets[selectedSocketIndex] : null;
  const selectedPeer = selectedSocket; // Legacy alias
  const selectedPeerId = selectedSocket?.connectionId || selectedSocket?.peerId || null;
  const selectedSessionId = selectedSocket?.sessionId || null;

  // Use a ref to always have the latest selectedPeerId for the getter
  const selectedPeerIdRef = useRef(selectedPeerId);
  selectedPeerIdRef.current = selectedPeerId;

  // Register the selectedPeerId getter with WebRTCContext
  // This allows features to get the current selected peer without stale closures
  useEffect(() => {
    if (registerSelectedPeerIdGetter) {
      registerSelectedPeerIdGetter(() => selectedPeerIdRef.current);
    }
  }, [registerSelectedPeerIdGetter]);

  /**
   * Generate a pairing code for a specific slot
   */
  const generateCodeForSlot = useCallback((slotIndex, role = 'full') => {
    if (slotIndex < 0 || slotIndex >= DEFAULT_SLOT_COUNT) {
      console.error('[SocketContext] Invalid slot index:', slotIndex);
      return;
    }

    const socket = sockets[slotIndex];
    if (socket.state !== SOCKET_STATE.EMPTY) {
      console.warn('[SocketContext] Cannot generate code for non-empty slot:', slotIndex, socket.state);
      return;
    }

    if (!wsConnected) {
      console.error('[SocketContext] Cannot generate pairing code: not connected');
      return;
    }

    console.log(`[SocketContext] Generating pairing code for slot ${slotIndex} (role=${role})`);
    pendingSlotRef.current = slotIndex;
    webrtcGeneratePairingCode(role);
  }, [sockets, wsConnected, webrtcGeneratePairingCode]);

  /**
   * Cancel a pending pairing code for a slot
   */
  const cancelPairingCode = useCallback((slotIndex) => {
    if (slotPairingCodes.has(slotIndex)) {
      console.log(`[SocketContext] Cancelling pairing code for slot ${slotIndex}`);
      setSlotPairingCodes(prev => {
        const newMap = new Map(prev);
        newMap.delete(slotIndex);
        return newMap;
      });
    }
  }, [slotPairingCodes]);

  /**
   * Select a socket for UI targeting
   */
  const selectSocket = useCallback((socketIndex) => {
    if (socketIndex === null) {
      setSelectedSocketIndex(null);
      console.log('[SocketContext] Deselected all sockets');
      return;
    }

    if (socketIndex < 0 || socketIndex >= DEFAULT_SLOT_COUNT) {
      console.error('[SocketContext] Invalid socket index:', socketIndex);
      return;
    }

    // Only allow selecting sockets that have a bound session
    const socket = sockets[socketIndex];
    if (socket.state === SOCKET_STATE.EMPTY || socket.state === SOCKET_STATE.PENDING) {
      console.warn('[SocketContext] Cannot select an unbound socket');
      return;
    }

    setSelectedSocketIndex(socketIndex);
    console.log(`[SocketContext] Selected socket ${socketIndex} (session: ${socket.shortName})`);
  }, [sockets]);

  /**
   * Unbind a session from a slot
   */
  const unbindSocket = useCallback((socketIndex) => {
    if (socketIndex < 0 || socketIndex >= DEFAULT_SLOT_COUNT) {
      console.error('[SocketContext] Invalid socket index:', socketIndex);
      return false;
    }

    const socket = sockets[socketIndex];

    // If it's a pending code, just cancel it
    if (socket.state === SOCKET_STATE.PENDING) {
      cancelPairingCode(socketIndex);
      return true;
    }

    // If it's a bound session, unbind it via WebRTC
    if (socket.sessionId && unbindSession) {
      console.log(`[SocketContext] Unbinding session ${socket.sessionId} from slot ${socketIndex}`);
      unbindSession(socket.sessionId);

      // If this was the selected socket, deselect it
      if (selectedSocketIndex === socketIndex) {
        setSelectedSocketIndex(null);
      }
      return true;
    }

    return false;
  }, [sockets, cancelPairingCode, unbindSession, selectedSocketIndex]);

  // Legacy compatibility: bindPeerToSocket (no-op, sessions are bound via pairing codes)
  const bindPeerToSocket = useCallback((socketIndex, peerId, peerName) => {
    console.warn('[SocketContext] bindPeerToSocket is deprecated - use pairing codes');
    return false;
  }, []);

  // Legacy compatibility: clearBindingByPeerId
  const clearBindingByPeerId = useCallback((peerId) => {
    console.warn('[SocketContext] clearBindingByPeerId is deprecated');
    return false;
  }, []);

  // Auto-select first online session if nothing is selected
  useEffect(() => {
    if (selectedSocketIndex !== null) return;

    const onlineSocket = sockets.find(s => s.state === SOCKET_STATE.BOUND_ONLINE);
    if (onlineSocket) {
      console.log(`[SocketContext] Auto-selecting first online socket: ${onlineSocket.index}`);
      setSelectedSocketIndex(onlineSocket.index);
    }
  }, [sockets, selectedSocketIndex]);

  // Deselect if selected socket becomes empty or pending.
  // Only after bound sessions have been loaded — before that, sockets are EMPTY because
  // the bound_sessions response hasn't arrived yet, not because the device was unbound.
  useEffect(() => {
    if (selectedSocketIndex === null) return;
    if (!wsConnected) return;
    if (!boundSessionsLoadedRef.current) return; // Sessions haven't loaded yet, don't deselect

    const socket = sockets[selectedSocketIndex];
    if (socket.state === SOCKET_STATE.EMPTY || socket.state === SOCKET_STATE.PENDING) {
      console.log('[SocketContext] Selected socket became unbound, deselecting');
      setSelectedSocketIndex(null);
    }
  }, [sockets, selectedSocketIndex, wsConnected]);

  const value = {
    // Socket data
    sockets,
    slotCount: DEFAULT_SLOT_COUNT,

    // Selection state
    selectedSocketIndex,
    selectedSocket,
    selectedPeer, // Legacy alias
    selectedPeerId, // Legacy alias (connectionId)
    selectedSessionId,

    // Pairing actions
    generateCodeForSlot,
    cancelPairingCode,

    // Actions
    unbindSocket,
    selectSocket,

    // Legacy actions (deprecated but kept for compatibility)
    bindPeerToSocket,
    clearBindingByPeerId,

    // State constants
    SOCKET_STATE
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
